/**
 * Due pill for a manual order (Phase 104, D-14): "Overdue · 12 Oct",
 * "Today", "Tomorrow" or a short date. Red when overdue or due within 2 days,
 * amber within the week, plain after that. Renders nothing without a due
 * date. Everything shows on the pill (an overdue order says since when), so
 * nothing hides behind a hover. Used on the Order Queue row and on the On
 * Hold tab.
 */
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatDayMonth, useDateFnsLocale } from '@/lib/format-dates';
import { localIsoDate } from '../lib/build-payload';
import { duePillModel } from '../lib/due-pill';

const URGENCY_VARIANT = {
  overdue_or_soon: 'error',
  this_week: 'warning',
  later: 'muted',
} as const;

interface DuePillProps {
  dueDate: string | null | undefined;
  className?: string;
}

export function DuePill({ dueDate, className }: DuePillProps) {
  const { t } = useTranslation('production');
  const locale = useDateFnsLocale();
  const model = duePillModel(dueDate, localIsoDate());
  if (!model || !dueDate) return null;

  const label =
    model.word === 'date'
      ? formatDayMonth(dueDate, locale)
      : model.word === 'overdue'
        ? t('orders.manual.due.overdueOn', { date: formatDayMonth(dueDate, locale) })
        : t(`orders.manual.due.${model.word}`);

  return (
    <Badge
      data-testid="due-pill"
      variant={URGENCY_VARIANT[model.urgency]}
      size="sm"
      className={cn('shrink-0 whitespace-nowrap leading-4 tabular-nums', className)}
    >
      <span className="sr-only">{t('orders.manual.due.srPrefix')} </span>
      {label}
    </Badge>
  );
}
